import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { TextButton } from "@/Components/ui/text-button";
import { Button } from "@/Components/ui/button";
import { LogoIcon } from "@/assets/icons/logo";
import { Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/use-translation";

export function Header({ page }: { page: string }) {
   const navigate = useNavigate();
   const { translation, localizePath } = useTranslation();
   const { header } = translation;
   const [scrolled, setScrolled] = useState(false);
   const [mobileOpen, setMobileOpen] = useState(false);

   useEffect(() => {
      const onScroll = () => setScrolled(window.scrollY > 10);
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
   }, []);

   const isCategoriesPage = page === "categories";
   const textClass =
      isCategoriesPage || scrolled ? "text-custom-dark" : "text-white";
   const bgClass = isCategoriesPage || scrolled ? "bg-white" : "bg-transparent";
   const btnVariant = isCategoriesPage || scrolled ? "l-white" : "l-dark";
   const logoColor = isCategoriesPage || scrolled ? "#07335A" : "#FFFFFF";
   const linkVariant =
      isCategoriesPage || scrolled ? "link-underline" : "link-underline-white";

   const navItems = [
      { key: "products", to: "/categories" },
      { key: "aboutUs", to: "/#about" },
      { key: "contacts", to: "/#contacts" },
   ] as const;

   return (
      <header
         className={cn(
            "fixed top-0 w-full z-50 transition-colors duration-300",
            bgClass,
            textClass
         )}
      >
         <div className="flex items-center justify-between px-4 py-3 sm:px-6 lg:px-10">
            {/* Logo */}
            <LogoIcon
               fill={logoColor}
               className="cursor-pointer"
               onClick={() => navigate(localizePath("/"))}
            />

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
               <nav className="flex gap-6">
                  {navItems.map(({ key, to }) => (
                     <TextButton
                        key={key}
                        variant={linkVariant}
                        className="block"
                     >
                        <a href={localizePath(to)}>{header.nav[key]}</a>
                     </TextButton>
                  ))}
               </nav>
               <Button
                  onClick={() => navigate(localizePath("/categories"))}
                  variant={btnVariant}
               >
                  {header.button}
               </Button>
            </div>

            {/* Mobile toggle */}
            <button
               className="md:hidden p-2 text-current"
               onClick={() => setMobileOpen((o) => !o)}
               aria-label={
                  mobileOpen ? header.aria.closeMenu : header.aria.openMenu
               }
            >
               {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
         </div>

         {/* Mobile dropdown */}
         <div
            className={cn(
               "md:hidden absolute top-full left-0 w-full bg-white shadow-md transition-max-h duration-300 overflow-hidden",
               mobileOpen ? "max-h-screen" : "max-h-0"
            )}
         >
            <nav className="flex flex-col px-4 py-4 gap-4">
               {navItems.map(({ key, to }) => (
                  <TextButton
                     key={key}
                     variant="link-underline"
                     className="text-custom-dark text-lg"
                     onClick={() => setMobileOpen(false)}
                  >
                     <a href={localizePath(to)}>{header.nav[key]}</a>
                  </TextButton>
               ))}
            </nav>
            <div className="px-4 pb-4">
               <Button
                  onClick={() => {
                     setMobileOpen(false);
                     navigate(localizePath("/categories"));
                  }}
                  variant="l-dark"
                  className="w-full"
               >
                  {header.button}
               </Button>
            </div>
         </div>
      </header>
   );
}
